/** @format */

/* eslint react-hooks/rules-of-hooks: "warn" */
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { parse, stringify } from 'qs';
import React, { useContext } from 'react';

import {
    formatModelPrice,
    getModelHrefFromSlug,
} from '@toucan/common/services/model/helpers';
import {
    QueryResponse,
    SearchListingModel,
} from '@toucan/common/services/search/types';

import { MarketContext } from '@toucan/client/global/market';
import { ModelListing } from '@toucan/client/global/model';
import { WrappedLink } from '@toucan/client/global/nextjs';

import { getModelListingQuery } from '@toucan/client/core/search';

import { LinkVariant } from '@toucan/client/shared/components/link';

import { SearchResultsType } from '@toucan/client/features/search/components/listing-page';
import { ModelCard } from '@toucan/client/features/search/components/model-card';

export function modelListingHelper(
    onCreateAlert?: (model: ModelListing) => void
) {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    const router = useRouter();

    const getLinkTemplatePath = (
        isNewModel: boolean,
        modelSlug: string,
        sku: string
    ) => {
        if (sku) {
            return `/product?slug=${modelSlug}&sku=${sku}`;
        }

        if (isNewModel) {
            return `/product?slug=${modelSlug}`;
        }

        return `/model?slug=${modelSlug}`;
    };

    /**
     * Converts data from the API into an object to be used by the ModelCard
     *
     * @param model The model data to map
     */
    const convertItemDataToModel = (model: SearchListingModel) => {
        const quantityInt = parseInt(
            model.productPriceCount?.values[0] || '0',
            10
        );

        const minPriceInt = parseInt(
            model.productPriceMinimum?.values[0] || '0',
            10
        );

        const maxPriceInt = parseInt(
            model.productPriceMaximum?.values[0] || '0',
            10
        );

        const modelSlug = model.modelUrlSegment?.values[0];
        const isNewModel =
            model.modelAvailableNew?.values[0]?.toLowerCase() === 'true';

        // Will ONLY be present on the object when there is only one model with one product returned from the search on a model listing page
        const sku = model.productSku?.values?.[0] ?? '';

        const linkTemplatePath = getLinkTemplatePath(
            isNewModel,
            modelSlug,
            sku
        );

        const linkHref = getModelHrefFromSlug(
            t,
            activeMarket.slug,
            modelSlug,
            sku
        );

        const formatPrice = formatModelPrice(activeMarket);

        return {
            linkTemplatePath,
            linkHref,
            id: parseInt(model.modelId?.values[0]) || undefined,
            quantity: quantityInt,
            image: model.modelImages?.values[0] || '',
            name: model.modelName?.values[0] || '',
            description: model.modelDescription?.values[0],
            priceMin: formatPrice(minPriceInt),
            priceMax: formatPrice(maxPriceInt),
        };
    };

    /**
     * Gets an array of model card/placeholder data to be applied to ModelCard components
     */
    function getModelCardData(
        searchResults: Omit<QueryResponse<SearchListingModel>, 'facets'>,
        numResultSkeletons: number
    ) {
        return [
            ...searchResults.results.map((model) => {
                return convertItemDataToModel(model);
            }),
            ...new Array(numResultSkeletons).fill(undefined),
        ];
    }

    /**
     * Gets either model cards displaying models or model cards used as placeholders
     * whilst data is loading
     *
     * @param searchResults the search results
     * @param numResultSkeletons the number of loading search results to display skeletons for
     * @param view The view the model cards should be displayed in
     *
     * @returns {ReactNode} The model cards to show
     */
    function getModelCards(
        searchResults: SearchResultsType<SearchListingModel>,
        numResultSkeletons: number
    ) {
        return getModelCardData(searchResults, numResultSkeletons).map(
            (model, index) => {
                const href = model?.linkTemplatePath || model?.linkHref || '';
                const key = `${index}-${model?.id}`;
                const queryObject = router.asPath
                    ? parse(router.asPath.split('?')[1])
                    : {};
                const { filterQuery, productPrice } = queryObject;
                const filterQueryObject =
                    typeof filterQuery == 'object'
                        ? (filterQuery as Record<string, string>)
                        : null;
                const queryString = stringify(
                    {
                        filterQuery: {
                            productPrice: filterQueryObject?.productPrice,
                            productConditionStarRating:
                                filterQueryObject?.productConditionStarRating,
                        },
                        productPrice,
                    },
                    {
                        addQueryPrefix: true,
                    }
                );

                return (
                    <div key={key} className="link-wrapper">
                        <WrappedLink
                            className="card-link"
                            data-testid="link-wrapper"
                            variant={LinkVariant.CUSTOM}
                            href={href}
                            as={`${model?.linkHref}${queryString}`}
                            disabled={!href}
                            passHref
                            hasOutlineOnFocus
                        >
                            <ModelCard
                                modelListing={model}
                                onCreateAlert={onCreateAlert}
                            />
                        </WrappedLink>
                    </div>
                );
            }
        );
    }

    return {
        getListingCards: getModelCards,
        getQuery: getModelListingQuery,
    };
}
